import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { CompassIcon as Compass } from "@/components/icons/lucide-compass";
import { FolderOpenIcon as FolderOpen } from "@/components/icons/lucide-folder-open";
import { FolderIcon as FolderIconClosed } from "@/components/icons/lucide-folder";
import { HeartIcon as Heart } from "@/components/icons/lucide-heart";
import { UsersIcon as Users } from "@/components/icons/lucide-users";
import { MessageSquareIcon as MessageSquare } from "@/components/icons/lucide-message-square";
import { SettingsIcon as Settings } from "@/components/icons/lucide-settings";
import { ShieldIcon as Shield } from "@/components/icons/lucide-shield";
import { BookOpenIcon as BookOpen } from "@/components/icons/lucide-book-open";
import { Home, PlusCircle, Puzzle, Check } from "lucide-react";
import { useUIStore } from "@/stores/ui-store";
import { WORKFLOW_EXPLORE_CATEGORIES } from "@/lib/workflow-explore-categories";
import { BuyMeACoffeeIcon } from "@/components/icons/simple-icons-buymeacoffee";
import { GoogleAdSense } from "@/components/ads/google-adsense";
import { GOOGLE_ADS } from "@/lib/google-ads";

const navigation = [
  { name: "Home", href: "/", icon: Home },
  { name: "Explore", href: "/explore", icon: Compass },
  { name: "Guides", href: "/guides", icon: BookOpen },
  {
    name: "My Workflows",
    href: "/dashboard/workflows",
    icon: FolderOpen,
    auth: true,
  },
  { name: "Favorites", href: "/dashboard/favorites", icon: Heart, auth: true },
  { name: "Following", href: "/dashboard/following", icon: Users, auth: true },
  { name: "Forums", href: "/forum", icon: MessageSquare },
  {
    name: "Collections",
    href: "/dashboard/collections",
    icon: FolderIconClosed,
    auth: true,
  },
];

export function Sidebar() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isAdmin } = useAuth();
  const { sidebarOpen, setSidebarOpen } = useUIStore();

  const exploreCategory =
    location.pathname === "/explore" ||
    location.pathname.startsWith("/explore/")
      ? searchParams.get("category")
      : null;

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r bg-background transition-transform duration-300 ease-in-out md:translate-x-0 md:static md:inset-auto md:z-auto md:block",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain p-4">
          {/* Quick action */}
          {isAuthenticated && (
            <div className="space-y-2 pb-4">
              <Button asChild className="w-full" variant="default">
                <Link to="/workflow/new">
                  <PlusCircle className="mr-2 h-4 w-4" />
                  New Workflow
                </Link>
              </Button>
              <Button asChild className="w-full" variant="outline">
                <Link to="/guides/new">
                  <BookOpen className="mr-2 h-4 w-4" />
                  New guide
                </Link>
              </Button>
            </div>
          )}

          {/* Main navigation */}
          <div className="space-y-1">
            {navigation.map((item) => {
              if (item.auth && !isAuthenticated) return null;

              const isActive =
                location.pathname === item.href ||
                location.pathname.startsWith(item.href + "/");
              const Icon = item.icon;

              return (
                <Link key={item.name} to={item.href}>
                  <Button
                    variant={isActive ? "secondary" : "ghost"}
                    className={cn(
                      "w-full justify-start",
                      isActive && "bg-muted font-medium",
                    )}
                  >
                    <Icon className="mr-3 h-4 w-4" />
                    {item.name}
                  </Button>
                </Link>
              );
            })}
          </div>

          {/* Admin Panel link */}
          {isAdmin && (
            <div className="pt-4">
              <Link to="/admin">
                <Button
                  variant={
                    location.pathname.startsWith("/admin")
                      ? "secondary"
                      : "ghost"
                  }
                  className={cn(
                    "w-full justify-start",
                    location.pathname.startsWith("/admin") &&
                      "bg-muted font-medium",
                  )}
                >
                  <Shield className="mr-3 h-4 w-4" />
                  Admin Panel
                </Button>
              </Link>
            </div>
          )}

          <div className="space-y-1 pt-6">
            {isAuthenticated ? (
              <>
                <Link to="/dashboard/settings">
                  <Button
                    variant={
                      location.pathname.startsWith("/dashboard/settings")
                        ? "secondary"
                        : "ghost"
                    }
                    className={cn(
                      "w-full justify-start",
                      location.pathname.startsWith("/dashboard/settings") &&
                        "bg-muted font-medium",
                    )}
                  >
                    <Settings className="mr-3 h-4 w-4" />
                    Settings
                  </Button>
                </Link>
                <Link to="/dashboard/plugin">
                  <Button
                    variant={
                      location.pathname.startsWith("/dashboard/plugin")
                        ? "secondary"
                        : "ghost"
                    }
                    className={cn(
                      "w-full justify-start gap-2",
                      location.pathname.startsWith("/dashboard/plugin") &&
                        "bg-muted font-medium",
                    )}
                  >
                    <Puzzle className="mr-3 h-4 w-4 shrink-0" />
                    <span className="min-w-0 flex-1 truncate text-left">
                      Plugin
                    </span>
                    <Check className="h-4 w-4 shrink-0 text-green-600" />
                  </Button>
                </Link>
              </>
            ) : null}
            <Button asChild variant="ghost" className="w-full justify-start">
              <a
                href="https://buymeacoffee.com/comfyflowan"
                target="_blank"
                rel="noopener noreferrer"
              >
                <BuyMeACoffeeIcon className="mr-3 h-4 w-4" />
                Buy Me a Coffee
              </a>
            </Button>
          </div>

          {/* Categories */}
          <div className="pt-6">
            <h3 className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Categories
            </h3>
            <div className="space-y-1">
              {WORKFLOW_EXPLORE_CATEGORIES.map(({ query, navLabel, Icon }) => {
                const isCategoryActive = exploreCategory === query;
                return (
                  <Link key={query} to={`/explore?category=${query}`}>
                    <Button
                      variant={isCategoryActive ? "secondary" : "ghost"}
                      className={cn(
                        "w-full justify-start",
                        isCategoryActive && "bg-muted font-medium",
                      )}
                    >
                      <Badge
                        variant="secondary"
                        className="mr-2 flex h-7 w-7 shrink-0 items-center justify-center p-0"
                      >
                        <Icon size={16} className="text-muted-foreground" />
                      </Badge>
                      {navLabel}
                    </Button>
                  </Link>
                );
              })}
            </div>
          </div>

        </nav>

        {GOOGLE_ADS.sidebar ? (
          <div className="shrink-0 border-t px-3 py-4">
            <GoogleAdSense
              slot={GOOGLE_ADS.sidebar}
              format="vertical"
              className="mx-auto max-w-[220px]"
            />
          </div>
        ) : null}
      </aside>
    </>
  );
}
